import React, { useState, useEffect, useRef } from 'react';
import { Icon, ICONS } from './icons';
import { useLocalization, type Language } from '../../config/localization';

interface WorkflowTileConfig {
  displayType?: 'single_image' | 'image_pair' | 'single_video' | 'slideshow' | null;
  imageUrl?: string;
  imageUrl2?: string;
  videoUrl?: string;
  comparisonMode?: 'slider' | 'auto_switch';
  autoSwitchDelay?: number;
  slideshowImages?: string[];
  slideshowDelay?: number;
  slideshowTransition?: 'instant' | 'fade' | 'slide';
}

interface WorkflowTag {
  id: number;
  name_en: string;
  name_ru: string;
  display_order: number;
}

interface WorkflowData {
  id: string;
  workflow_id: number;
  title: string;
  category: string;
  description?: string;
  tile_config?: WorkflowTileConfig | null;
  tags?: WorkflowTag[];
  display_status?: 'active' | 'new' | 'archived';
}

interface WorkflowTileProps {
  workflow: WorkflowData;
  isSelected: boolean;
  onSelect: () => void;
  language: Language;
  isFavorite?: boolean;
  onToggleFavorite?: (workflowId: number) => void;
  size?: 'small' | 'medium' | 'large';
  showTags?: boolean;
}

export default function WorkflowTile({
  workflow,
  isSelected,
  onSelect,
  language,
  isFavorite = false,
  onToggleFavorite,
  size = 'small',
  showTags = false
}: WorkflowTileProps) {
  const t = useLocalization(language);
  const tileConfig = workflow.tile_config;
  const [isHovering, setIsHovering] = useState(false);
  const [showSecondImage, setShowSecondImage] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const videoRef = useRef<HTMLVideoElement>(null);
  const autoSwitchTimeout = useRef<NodeJS.Timeout>();
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [sliderWidth, setSliderWidth] = useState(0);

  // Slideshow states
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const slideshowInterval = useRef<NodeJS.Timeout>();

  // Update slider width when component mounts or ref changes
  useEffect(() => {
    if (sliderRef.current) {
      setSliderWidth(sliderRef.current.offsetWidth);
    }
  }, [tileConfig]);

  // Handle auto-switch for image pairs
  useEffect(() => {
    if (
      isHovering &&
      tileConfig?.displayType === 'image_pair' &&
      tileConfig.comparisonMode === 'auto_switch'
    ) {
      autoSwitchTimeout.current = setTimeout(() => {
        setShowSecondImage(true);
      }, tileConfig.autoSwitchDelay || 2000);
    } else {
      setShowSecondImage(false);
      if (autoSwitchTimeout.current) {
        clearTimeout(autoSwitchTimeout.current);
      }
    }

    return () => {
      if (autoSwitchTimeout.current) {
        clearTimeout(autoSwitchTimeout.current);
      }
    };
  }, [isHovering, tileConfig]);

  // Handle video playback
  useEffect(() => {
    if (tileConfig?.displayType === 'single_video' && videoRef.current) {
      if (isHovering) {
        videoRef.current.play().catch(() => {
          // Ignore autoplay errors
        });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovering, tileConfig]);

  // Handle slideshow auto-play
  useEffect(() => {
    if (
      isHovering &&
      tileConfig?.displayType === 'slideshow' &&
      tileConfig.slideshowImages &&
      tileConfig.slideshowImages.length > 1
    ) {
      const delay = tileConfig.slideshowDelay || 1000;
      const totalSlides = tileConfig.slideshowImages.length;

      slideshowInterval.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentSlideIndex((prevIndex) => prevIndex + 1);
      }, delay);
    } else {
      // Reset to first slide when not hovering
      setCurrentSlideIndex(0);
      setIsTransitioning(true);
      if (slideshowInterval.current) {
        clearInterval(slideshowInterval.current);
      }
    }

    return () => {
      if (slideshowInterval.current) {
        clearInterval(slideshowInterval.current);
      }
    };
  }, [isHovering, tileConfig]);

  // Handle infinite loop for slide transition
  useEffect(() => {
    if (
      tileConfig?.displayType === 'slideshow' &&
      tileConfig.slideshowTransition === 'slide' &&
      tileConfig.slideshowImages &&
      tileConfig.slideshowImages.length > 1
    ) {
      const totalSlides = tileConfig.slideshowImages.length;

      // When we reach the cloned first slide (index = totalSlides)
      if (currentSlideIndex === totalSlides) {
        // Wait for transition to complete, then reset to real first slide without animation
        setTimeout(() => {
          setIsTransitioning(false);
          setCurrentSlideIndex(0);
        }, 500); // Match the transition duration
      }

      // Re-enable transitions after reset
      if (currentSlideIndex === 0 && !isTransitioning) {
        setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
      }
    }
  }, [currentSlideIndex, tileConfig, isTransitioning]);

  // Handle slider dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (tileConfig?.displayType === 'image_pair' && tileConfig.comparisonMode === 'slider') {
      isDragging.current = true;
      updateSliderPosition(e);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (sliderRef.current) {
      // Update slider width if not set yet
      if (sliderWidth === 0) {
        setSliderWidth(sliderRef.current.offsetWidth);
      }
      // Update slider position when dragging OR when hovering (for better UX in modal)
      if (isDragging.current || isHovering) {
        updateSliderPosition(e);
      }
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const updateSliderPosition = (e: React.MouseEvent) => {
    if (sliderRef.current) {
      const rect = sliderRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    }
  };

  return (
    <div
      className={`
        relative bg-gray-800 rounded-lg cursor-pointer
        transition-all duration-200 hover:scale-105
        ${isSelected ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-600'}
        ${isHovering ? 'z-50' : 'z-0'}
      `}
      onClick={onSelect}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Media Display Area */}
      <div className="aspect-video bg-gray-900 relative overflow-hidden rounded-t-lg">
        {/* Single Image */}
        {tileConfig?.displayType === 'single_image' && tileConfig.imageUrl && (
          <img
            src={tileConfig.imageUrl}
            alt={workflow.title}
            className="w-full h-full object-cover"
          />
        )}

        {/* Image Pair with Slider */}
        {tileConfig?.displayType === 'image_pair' &&
          tileConfig.comparisonMode === 'slider' &&
          tileConfig.imageUrl &&
          tileConfig.imageUrl2 && (
          <div
            ref={sliderRef}
            className="relative w-full h-full select-none"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* After image (background) */}
            <img
              src={tileConfig.imageUrl2}
              alt={`${workflow.title} - After`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Before image (clipped) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${sliderPosition}%` }}
            >
              <img
                src={tileConfig.imageUrl}
                alt={`${workflow.title} - Before`}
                className="absolute top-0 left-0 h-full object-cover"
                style={{
                  width: sliderWidth > 0 ? `${sliderWidth}px` : '100%',
                  maxWidth: 'none'
                }}
              />
            </div>
            {/* Slider handle */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-white to-purple-500 cursor-ew-resize shadow-2xl"
              style={{ left: `${sliderPosition}%` }}
            >
              {/* Handle circle */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full border-2 border-white flex items-center justify-center shadow-2xl transition-transform hover:scale-110">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M6 5L3 8l3 3V5z" />
                  <path d="M10 5l3 3-3 3V5z" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Image Pair with Auto-switch */}
        {tileConfig?.displayType === 'image_pair' &&
          tileConfig.comparisonMode === 'auto_switch' &&
          tileConfig.imageUrl &&
          tileConfig.imageUrl2 && (
          <div className="relative w-full h-full">
            <img
              src={showSecondImage ? tileConfig.imageUrl2 : tileConfig.imageUrl}
              alt={workflow.title}
              className="w-full h-full object-cover transition-opacity duration-300"
            />
          </div>
        )}

        {/* Single Video */}
        {tileConfig?.displayType === 'single_video' && tileConfig.videoUrl && (
          <video
            ref={videoRef}
            src={tileConfig.videoUrl}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
          />
        )}

        {/* Slideshow */}
        {tileConfig?.displayType === 'slideshow' &&
          tileConfig.slideshowImages &&
          tileConfig.slideshowImages.length > 0 && (
          <div className="relative w-full h-full overflow-hidden">
            {/* Instant transition - simple image swap */}
            {(!tileConfig.slideshowTransition || tileConfig.slideshowTransition === 'instant') && (
              <img
                src={tileConfig.slideshowImages[currentSlideIndex]}
                alt={`${workflow.title} - Slide ${currentSlideIndex + 1}`}
                className="w-full h-full object-cover"
              />
            )}

            {/* Fade transition - crossfade effect */}
            {tileConfig.slideshowTransition === 'fade' && (
              <>
                {tileConfig.slideshowImages.map((imageUrl, index) => (
                  <img
                    key={index}
                    src={imageUrl}
                    alt={`${workflow.title} - Slide ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                      index === currentSlideIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </>
            )}

            {/* Slide transition - horizontal slide effect */}
            {tileConfig.slideshowTransition === 'slide' && (
              <div
                className={`flex h-full ${isTransitioning ? 'transition-transform duration-500 ease-in-out' : ''}`}
                style={{
                  width: `${(tileConfig.slideshowImages.length + 1) * 100}%`,
                  transform: `translateX(-${(currentSlideIndex * 100) / (tileConfig.slideshowImages.length + 1)}%)`
                }}
              >
                {/* Render all slides */}
                {tileConfig.slideshowImages.map((imageUrl, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 h-full"
                    style={{ width: `${100 / (tileConfig.slideshowImages!.length + 1)}%` }}
                  >
                    <img
                      src={imageUrl}
                      alt={`${workflow.title} - Slide ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                {/* Clone first slide at the end for infinite loop */}
                <div
                  key="clone"
                  className="flex-shrink-0 h-full"
                  style={{ width: `${100 / (tileConfig.slideshowImages!.length + 1)}%` }}
                >
                  <img
                    src={tileConfig.slideshowImages[0]}
                    alt={`${workflow.title} - Slide 1 (clone)`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fallback if no tile config */}
        {!tileConfig?.displayType && (
          <div className="w-full h-full flex items-center justify-center text-gray-600">
            <Icon name={ICONS.IMAGE} size={48} />
          </div>
        )}

        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}

        {/* Favorite star button */}
        {onToggleFavorite && (isHovering || isFavorite) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(workflow.workflow_id);
            }}
            className={`absolute top-2 left-2 p-1.5 rounded-full transition-all duration-200 z-10 ${
              isFavorite
                ? 'bg-yellow-500/90 hover:bg-yellow-600'
                : 'bg-black/40 hover:bg-black/60 backdrop-blur-sm'
            }`}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Icon
              name={isFavorite ? ICONS.STAR_FILLED : ICONS.STAR}
              size={20}
              className={isFavorite ? 'text-white' : 'text-white/90'}
            />
          </button>
        )}
      </div>

      {/* Workflow Info */}
      <div className={`bg-gray-800 rounded-b-lg ${size === 'large' ? 'p-4' : size === 'medium' ? 'p-3.5' : 'p-3'}`}>
        <h4 className={`font-semibold text-white truncate ${
          size === 'large' ? 'text-base' : size === 'medium' ? 'text-sm' : 'text-sm'
        }`}>
          {workflow.title}
        </h4>

        {/* Tags - Always show when available */}
        {showTags && (workflow.tags?.length || workflow.display_status === 'new' || workflow.display_status === 'archived') && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {workflow.display_status === 'new' && (
              <span className="px-2 py-0.5 bg-green-900/30 border border-green-600/30 rounded text-xs text-green-400">
                {t.workflow?.newBadge || (language === 'ru' ? 'Новое' : 'New')}
              </span>
            )}
            {workflow.display_status === 'archived' && (
              <span className="px-2 py-0.5 bg-gray-700/30 border border-gray-600/30 rounded text-xs text-gray-400">
                {t.workflow?.archivedBadge || (language === 'ru' ? 'Архивный' : 'Archived')}
              </span>
            )}
            {workflow.tags?.map((tag) => (
              <span
                key={tag.id}
                className="px-2 py-0.5 bg-purple-900/30 border border-purple-500/30 rounded text-xs text-purple-300"
              >
                {language === 'ru' ? tag.name_ru : tag.name_en}
              </span>
            ))}
          </div>
        )}

        {/* Description - Always show for medium and large */}
        {workflow.description && (size === 'medium' || size === 'large') && (
          <p className={`text-gray-400 leading-relaxed mt-2 ${
            size === 'large' ? 'text-sm line-clamp-3' : 'text-xs line-clamp-2'
          }`}>
            {workflow.description}
          </p>
        )}
      </div>
    </div>
  );
}
